import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

const STORAGE_KEY = "whoson_cookie_consent";

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    // Only show if user hasn't already accepted
    const accepted = localStorage.getItem(STORAGE_KEY);
    if (!accepted) {
      const timer = setTimeout(() => setVisible(true), 1200);
      return () => clearTimeout(timer);
    }
  }, []);

  const accept = () => {
    localStorage.setItem(STORAGE_KEY, "1");
    setVisible(false);
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ y: 80, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 80, opacity: 0 }}
          transition={{ type: "spring", damping: 28, stiffness: 300 }}
          data-testid="cookie-banner"
          className="fixed bottom-16 md:bottom-4 left-0 right-0 z-[90] px-4 pointer-events-none"
        >
          <div className="max-w-2xl mx-auto bg-[#141322] border border-purple-500/25 rounded-2xl shadow-[0_8px_40px_rgba(0,0,0,0.5)] pointer-events-auto overflow-hidden">
            {/* Gradient top accent */}
            <div className="h-0.5 bg-gradient-to-r from-violet-500 via-purple-400 to-cyan-400" />

            <div className="flex items-center gap-3 p-4">
              {/* Cookie icon — emoji-free, just a small gradient circle */}
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-500/20 to-orange-500/10 border border-amber-500/25 flex items-center justify-center shrink-0 text-lg select-none" aria-hidden>
                🍪
              </div>

              {/* Text */}
              <div className="flex-1 min-w-0">
                <p className="text-sm text-slate-200 leading-snug">
                  <span className="font-semibold text-white">We use cookies only to keep you signed in.</span>
                  {" "}
                  <span className="text-slate-400 text-xs">
                    No tracking. No ads. No third-party analytics. Just the session cookie that makes the app work.
                  </span>
                </p>
              </div>

              {/* Actions */}
              <div className="flex items-center gap-2 shrink-0">
                <Link
                  to="/privacy"
                  className="hidden sm:inline text-xs text-purple-400 hover:text-purple-300 transition-colors whitespace-nowrap underline underline-offset-2"
                >
                  Privacy Policy
                </Link>
                <button
                  data-testid="cookie-accept-btn"
                  onClick={accept}
                  className="px-4 py-2 bg-gradient-to-r from-violet-600 to-cyan-500 text-white text-sm font-bold rounded-xl hover:opacity-90 active:scale-95 transition-all whitespace-nowrap"
                >
                  Got it
                </button>
                <button
                  data-testid="cookie-dismiss-btn"
                  onClick={accept}
                  aria-label="Dismiss"
                  className="text-slate-600 hover:text-slate-400 transition-colors p-1 shrink-0"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
